import React, { useState, type SyntheticEvent } from 'react';
import debounce from 'lodash/debounce';
import {
	graphql,
	useMutation,
	useRefetchableFragment,
	useFragment,
	usePreloadedQuery,
	type PreloadedQuery,
} from 'react-relay';
import type { FetchQueryFetchPolicy, RecordSourceSelectorProxy } from 'relay-runtime';
import { Stack, Flex, Box, Text, Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import SearchIcon from '@atlaskit/icon/core/search';
import Textfield from '@atlaskit/textfield';
import Spinner from '@atlaskit/spinner';
import { AgentAvatar } from '@atlaskit/rovo-agent-components';
import AngleBracketsIcon from '@atlaskit/icon/core/angle-brackets';
import {
	AgentPickerBulkOpenMenuExpSuccess,
	AgentPickerSingleIssueOpenMenuExpSuccess,
} from '@atlassian/jira-autodev-common/src/services/experience-trackers/agent-picker/index.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { type UIAnalyticsEvent, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type {
	utils_triggerRovoAgentMutation,
	utils_triggerRovoAgentMutation$data,
} from '@atlassian/jira-relay/src/__generated__/utils_triggerRovoAgentMutation.graphql';
import type {
	pickerContent_deleteJobMutation,
	pickerContent_deleteJobMutation$data,
} from '@atlassian/jira-relay/src/__generated__/pickerContent_deleteJobMutation.graphql';
import REFETCH_QUERY, {
	type pickerContentRefetchQuery,
} from '@atlassian/jira-relay/src/__generated__/pickerContentRefetchQuery.graphql';
import { useSuspenselessRefetch } from '@atlassian/jira-issue-hooks/src/services/use-suspenseless-refetch/index.tsx';

import type { pickerContent_autodevAgentPicker_jobRef$key } from '@atlassian/jira-relay/src/__generated__/pickerContent_autodevAgentPicker_jobRef.graphql';
import type { pickerContent_autodevAgentPicker_agentsFragRef$key } from '@atlassian/jira-relay/src/__generated__/pickerContent_autodevAgentPicker_agentsFragRef.graphql';
import { fireAutodevErrorAnalytics } from '@atlassian/jira-autodev-common/src/common/utils.tsx';
import { Interactions } from '@atlassian/jira-autodev-common/src/common/constants.tsx';
import { usePathParam } from '@atlassian/react-resource-router';
import type { pickerContentQuery as pickerContentQueryType } from '@atlassian/jira-relay/src/__generated__/pickerContentQuery.graphql';
import { RestartConfirmationModal } from './restart-confirmation-modal/index.tsx';
import messages from './messages.tsx';
import { EmptyAgentResult } from './empty-agent-result/index.tsx';

import {
	useOnTriggerAgentMutationError,
	useOnTrigggerAgentMutationCompleted,
	onDeleteJobMutationUpdater,
	emptyJobListStore,
	InvokeAgentMutation,
	triggerAutodevSlowEndpoint,
	triggerAutodevBulkSlowEndpoint,
	useOnTriggerAgentInBulkMutationError,
	useOnTrigggerAgentInBulkMutationCallbacks,
} from './utils.tsx';
import { BULK_LIMIT } from './constants.tsx';
import { BrowseTemplateModalDropdownItem } from './browse-template-modal-dropdown-item/index.tsx';
import { CreateDeveloperAgentDropdownItem } from './create-developer-agent-modal-dropdown-item/index.tsx';
import { GoodRankIndictor } from './good-rank-indicator/index.tsx';

// Optional props are mostly due to bulk triggering in Backlog, as props are not needed at all for Backlog
export type Props = {
	jobRef: pickerContent_autodevAgentPicker_jobRef$key | null;
	agentsFragRef: pickerContent_autodevAgentPicker_agentsFragRef$key | null;
	issueAris: string[];
	jobListRelayStoreId: string | null;
	setIsTriggeringAutodevInprogress?: (isOpen: boolean) => void;
	onRefreshJob?: (networkPolicy: FetchQueryFetchPolicy, onCompleted: () => void) => void;
	onRunAutodevClicked?: (
		event: SyntheticEvent | KeyboardEvent,
		analyticEvent: UIAnalyticsEvent,
	) => void;
	onClose?: () => void;
};

type WrapperProps = Omit<Props, 'agentsFragRef'> & {
	queryRef: PreloadedQuery<pickerContentQueryType>;
};

export const SearchFieldIcon = () => (
	<Box xcss={searchIconWrapperStyles}>
		<SearchIcon spacing="none" label="" color={token('color.icon')} />
	</Box>
);

export const PickerContentLoading = () => (
	<Flex xcss={loadingWrapperStyles} alignItems="center" justifyContent="center">
		<Spinner size="medium" />
	</Flex>
);

export const AgentPickerQueryWrapper = (props: WrapperProps) => {
	const data = usePreloadedQuery(
		graphql`
			query pickerContentQuery($cloudId: ID!, $issueAris: [ID!]!) {
				...pickerContent_autodevAgentPicker_agentsFragRef @arguments(issueAris: $issueAris)
			}
		`,
		props.queryRef,
	);

	return <PickerContent {...props} agentsFragRef={data} />;
};

export const PickerContent = ({
	jobRef,
	onRefreshJob,
	agentsFragRef,
	setIsTriggeringAutodevInprogress,
	issueAris,
	jobListRelayStoreId,
	onRunAutodevClicked,
	onClose,
}: Props) => {
	const { formatMessage } = useIntl();
	// The standard URL must have projectKey path param
	const [projectKey = ''] = usePathParam('projectKey');
	const cloudId = useCloudId();
	const job = useFragment<pickerContent_autodevAgentPicker_jobRef$key>(
		graphql`
			fragment pickerContent_autodevAgentPicker_jobRef on JiraAutodevJob {
				id
			}
		`,
		jobRef,
	);
	const currentJobId = job?.id ?? null;
	const [isRestartConfirmationModalOpen, setIsRestartConfirmationModalOpen] =
		useState<boolean>(false);
	// This is to distinguish between real empty state or when user delete search string to empty
	// This is important(DO NOT REMOVE), not to make UI flakers when search string is removed from something to none
	const [hasBeenSearched, setHasBeenSearched] = useState<boolean>(false);
	const [selectedAgent, setSelectedAgent] = useState<{
		agentId: string;
		agentName: string;
		event: SyntheticEvent | KeyboardEvent;
		analyticEvent: UIAnalyticsEvent;
	} | null>(null);
	const [agentSearchStr, setAgentSearchStr] = useState<string>('');

	const onTriggerMutationCompleted = useOnTrigggerAgentMutationCompleted(
		issueAris[0],
		() => {
			setIsTriggeringAutodevInprogress?.(true);
			// Forcibly refresh the job after triggering mutation is finished, until BE does this in triggeringAgent mutation in the future.
			onRefreshJob?.('network-only', () => setIsTriggeringAutodevInprogress?.(false));
		},
		setIsTriggeringAutodevInprogress,
	);
	const onTriggerMutationError = useOnTriggerAgentMutationError(issueAris[0]);
	const {
		onTriggerMutationInBulkCompleted,
		onTriggerMutationInBulkStarted,
		onTriggerMutationInBulkOverLimit,
	} = useOnTrigggerAgentInBulkMutationCallbacks(issueAris, projectKey);
	const onTriggerMutationInBulkError = useOnTriggerAgentInBulkMutationError(issueAris, projectKey);

	const [data, refetch] = useRefetchableFragment<
		pickerContentRefetchQuery,
		pickerContent_autodevAgentPicker_agentsFragRef$key
	>(
		graphql`
			fragment pickerContent_autodevAgentPicker_agentsFragRef on Query
			@refetchable(queryName: "pickerContentRefetchQuery")
			@argumentDefinitions(
				searchStr: { type: "String" }
				issueAris: { type: "[ID!]" }
				isVerticalsAgentPickerEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/autodev-is-verticals-agent-picker-enabled.relayprovider"
				}
			) {
				devai_autodevRovoAgents(
					cloudId: $cloudId
					first: 20
					after: null
					query: $searchStr
					issueIds: $issueAris
				) @include(if: $isVerticalsAgentPickerEnabled) @optIn(to: "DevAiVerticalAutodev") {
					isRankingLimitApplied
					edges @required(action: THROW) {
						node @required(action: THROW) {
							name @required(action: THROW)
							id @required(action: THROW)
							description
							identityAccountId
							externalConfigReference
							rankCategory
						}
					}
				}

				devai_autodevRovoTemplates: devai_autodevRovoAgents(
					cloudId: $cloudId
					first: 1
					templatesFilter: ONLY
				) @include(if: $isVerticalsAgentPickerEnabled) @optIn(to: "DevAiVerticalAutodev") {
					edges {
						node {
							id
						}
					}
				}
			}
		`,
		agentsFragRef,
	);

	const [triggerAutodev] = useMutation<utils_triggerRovoAgentMutation>(InvokeAgentMutation);

	const [deleteJob] = useMutation<pickerContent_deleteJobMutation>(graphql`
		mutation pickerContent_deleteJobMutation($issueAri: ID!, $jobId: ID!) {
			jira {
				devOps {
					deleteAutodevJob(issueAri: $issueAri, jobId: $jobId) {
						success
						errors {
							message
						}
					}
				}
			}
		}
	`);

	const [suspenselessRefetch, isRefetchLoading] = useSuspenselessRefetch(REFETCH_QUERY, refetch, {
		fetchPolicy: 'store-or-network', // Assume that agent is rarely changed, therefore choosing store-or-network
	});

	if (data == null) {
		return null;
	}

	const onSearchStrChange = debounce((searchStr: string) => {
		if (searchStr !== '') {
			setHasBeenSearched(true);
		}
		setAgentSearchStr(searchStr);
		suspenselessRefetch({
			searchStr,
			cloudId,
			issueAris,
			isVerticalsAgentPickerEnabled: fg('autodev_verticals_issueview_agent_dropdown_enabled'),
		});
	}, 300);

	const agents = (data.devai_autodevRovoAgents?.edges ?? [])
		.filter(Boolean)
		.map((edge) => edge.node);

	const hasAutodevTemplates =
		(data.devai_autodevRovoTemplates?.edges ?? []).filter(Boolean).length > 0;

	const triggerAutodevJob = (
		agentId: string,
		agentName: string,
		analyticsEvent: UIAnalyticsEvent,
	): void => {
		setIsTriggeringAutodevInprogress?.(true);
		// We try to maintain only one job per issue as per the requirement. If there is an existing job, we try to delete it together.
		// In the future when the mutation is moved to devai-service, we will delete job in triggerAgent mutation in BE, but for now we are deleting in FE.
		if (currentJobId != null) {
			const commonAttrs = {
				id: 'deleteJobTriggeringRovoAgent',
				jobId: currentJobId,
				agentId,
			};
			deleteJob({
				variables: {
					issueAri: issueAris[0],
					jobId: currentJobId,
				},
				onCompleted: (response: pickerContent_deleteJobMutation$data) => {
					const result = response.jira?.devOps?.deleteAutodevJob;
					if (result?.success !== true) {
						fireAutodevErrorAnalytics({
							...commonAttrs,
							error: new Error(result?.errors?.map((error) => error?.message).join(', ')),
						});
					}
				},
				onError: (error: Error) => {
					fireAutodevErrorAnalytics({
						...commonAttrs,
						error,
					});
				},
				updater: (
					store: RecordSourceSelectorProxy,
					response: pickerContent_deleteJobMutation$data | undefined | null,
				) => {
					onDeleteJobMutationUpdater(
						store,
						response?.jira?.devOps?.deleteAutodevJob?.success ?? false,
						jobListRelayStoreId,
					);
				},
				optimisticUpdater: (store: RecordSourceSelectorProxy) => {
					emptyJobListStore(store, jobListRelayStoreId);
				},
			});
		}

		if (fg('autodev_-_invoke_agent_slow_endpoint')) {
			// We will probably need a better way than this, but this should be pretty reliable for now. onRefreshJob is only needed in the issue view.
			// Intentionally defining named variable to indicate the meaning
			// Even for a single issue, we need to go to bulk run experience, since the result feedback UX differs.
			const isFromBacklog = onRefreshJob == null;
			if (isFromBacklog) {
				// We limit this to 20 for now. https://hello.atlassian.net/wiki/spaces/AIDO/pages/4966188488
				if (issueAris.length > BULK_LIMIT) {
					onTriggerMutationInBulkOverLimit(issueAris.length);
					return;
				}

				onClose?.();
				onTriggerMutationInBulkStarted(agentName, issueAris.length);
				triggerAutodevBulkSlowEndpoint({
					agentId,
					agentName,
					issueCount: issueAris.length,
					issueAris,
					onTriggerMutationInBulkCompleted,
					onTriggerMutationInBulkError,
				});
				return;
			}

			triggerAutodevSlowEndpoint({
				agentId,
				issueAris,
				onTriggerMutationCompleted,
				onTriggerMutationError,
				setIsTriggeringAutodevInprogress,
			});
			fireUIAnalytics(analyticsEvent, 'autodevRunAgent', {
				agentId,
			});
		} else {
			// Bulk does not work in this FG off case.
			const start = Date.now();
			triggerAutodev({
				variables: { issueAri: issueAris[0], agentId },
				onCompleted: (mutationResponse: utils_triggerRovoAgentMutation$data) => {
					const duration = Date.now() - start;
					onTriggerMutationCompleted(mutationResponse, agentId, duration);
				},
				onError: (e: Error) => {
					const duration = Date.now() - start;
					setIsTriggeringAutodevInprogress?.(false);
					onTriggerMutationError(agentId, duration, e);
				},
			});

			fireUIAnalytics(analyticsEvent, 'autodevRunAgent', {
				agentId,
			});
		}
	};

	const runAutodev = (
		event: SyntheticEvent | KeyboardEvent,
		analyticEvent: UIAnalyticsEvent,
	): void => {
		// We try to maintain only one job per issue as per the requirement. If there is an existing job, we try to delete it together.
		// In the future when the mutation is moved to devai-service, we will delete job in triggerAgent mutation in BE, but for now we are deleting in FE.
		if (currentJobId != null) {
			const commonAttrs = {
				id: 'deleteJobTriggeringRovoAgent',
				jobId: currentJobId,
			};
			deleteJob({
				variables: {
					issueAri: issueAris[0],
					jobId: currentJobId,
				},
				onCompleted: (response: pickerContent_deleteJobMutation$data) => {
					const result = response.jira?.devOps?.deleteAutodevJob;
					if (result?.success !== true) {
						fireAutodevErrorAnalytics({
							...commonAttrs,
							error: new Error(result?.errors?.map((error) => error?.message).join(', ')),
						});
					}

					// It is important to trigger this when deletion is succesfully done, otherwise we will open Autodev modal with the existing job.
					onRunAutodevClicked?.(event, analyticEvent);
				},
				onError: (error: Error) => {
					fireAutodevErrorAnalytics({
						...commonAttrs,
						error,
					});
				},
				updater: (
					store: RecordSourceSelectorProxy,
					response: pickerContent_deleteJobMutation$data | undefined | null,
				) => {
					onDeleteJobMutationUpdater(
						store,
						response?.jira?.devOps?.deleteAutodevJob?.success ?? false,
						jobListRelayStoreId,
					);
				},
				optimisticUpdater: (store: RecordSourceSelectorProxy) => {
					emptyJobListStore(store, jobListRelayStoreId);
				},
			});
		} else {
			onRunAutodevClicked?.(event, analyticEvent);
		}
	};

	const onTriggerAutodevClicked = (
		agentId: string,
		agentName: string,
		event: SyntheticEvent | KeyboardEvent,
		analyticEvent: UIAnalyticsEvent,
	) => {
		if (currentJobId != null) {
			setIsRestartConfirmationModalOpen(true);
			setSelectedAgent({ agentId, agentName, event, analyticEvent });
		} else {
			if (agentId === 'AUTODEV') {
				runAutodev(event, analyticEvent);
			} else {
				triggerAutodevJob(agentId, agentName, analyticEvent);
			}
			onClose?.();
		}
	};

	const cancelRestartAutodev = () => {
		setSelectedAgent(null);
		setIsRestartConfirmationModalOpen(false);
		onClose?.();
	};

	const restartAutodev = () => {
		if (selectedAgent == null) {
			return;
		}

		if (selectedAgent.agentId === 'AUTODEV') {
			runAutodev(selectedAgent.event, selectedAgent.analyticEvent);
		} else {
			triggerAutodevJob(
				selectedAgent.agentId,
				selectedAgent.agentName,
				selectedAgent.analyticEvent,
			);
		}
		setSelectedAgent(null);
		setIsRestartConfirmationModalOpen(false);
		onClose?.();
	};

	const isEmptyState = agents.length === 0 && agentSearchStr === '' && !hasBeenSearched;
	const isRankingLimitApplied = data.devai_autodevRovoAgents?.isRankingLimitApplied === true;

	return (
		<ShortcutScope>
			<Box xcss={contentWrapper}>
				{!isEmptyState && (
					<>
						<Box xcss={searchTextWrapperStyles}>
							<Textfield
								role="searchbox"
								label={formatMessage(messages.triggerPlaceholder)}
								onChange={({ currentTarget: { value } }) => {
									onSearchStrChange(value);
								}}
								elemBeforeInput={<SearchFieldIcon />}
								placeholder={formatMessage(messages.triggerPlaceholder)}
								testId="autodev-agent-picker.ui.picker-content.agent-search-input"
							/>
						</Box>
						<Flex alignItems="center" justifyContent="space-between" xcss={agentLabelWrapperStyles}>
							<Text size="UNSAFE_small" weight="bold">
								{formatMessage(
									isRankingLimitApplied ? messages.codingAgents : messages.suggestedAgents,
								)}
							</Text>
						</Flex>
					</>
				)}
				{!isEmptyState && (
					<Flex direction="column" xcss={agentListStyles}>
						{agents.length === 0 && <EmptyAgentResult />}
						{agents.map((agent) => (
							<Pressable
								key={agent.id}
								xcss={pressableButtonStyles}
								padding="space.075"
								onClick={(_event, _analyticEvent) => {
									onTriggerAutodevClicked(agent.id, agent.name, _event, _analyticEvent);
								}}
								testId="autodev-agent-picker.ui.picker-content.suggested-agent"
							>
								<Flex xcss={iconWrapperStyles} alignItems="center" justifyContent="center">
									<AgentAvatar
										// this plays important rule to determine the avatar icon and its color. This is exactly same as how BrowseAgent modal uses AgentAvatar component
										agentIdentityAccountId={agent.identityAccountId ?? null}
										agentId={agent.id}
										size="small"
										showBorder={false}
										// This will be used to recognize OOTB agents
										agentNamedId={agent.externalConfigReference ?? undefined}
									/>
								</Flex>
								<Stack xcss={titleSubTitleWrapperStyles}>
									<Flex alignItems="center" justifyContent="space-between">
										<Box as="span" xcss={truncateTextStyles}>
											{agent.name}
										</Box>
										{agent.rankCategory === 'GOOD_MATCH' && <GoodRankIndictor />}
									</Flex>
									<Box as="span" xcss={[subLabelStyles, truncateTextStyles]}>
										{agent.description ?? ''}
									</Box>
								</Stack>
							</Pressable>
						))}
						<Flex
							xcss={
								isRefetchLoading
									? [spinnerWrapperStyles]
									: [spinnerWrapperStyles, spinnerWrapperHiddenStyles]
							}
							justifyContent="center"
							alignItems="center"
						>
							{/* It is important to NOT render Spinner not to mess up UFO metrics */}
							{isRefetchLoading && <Spinner size="medium" />}
						</Flex>
					</Flex>
				)}
				<BrowseTemplateModalDropdownItem />
				{hasAutodevTemplates && <CreateDeveloperAgentDropdownItem />}
				{onRunAutodevClicked != null && (
					<Pressable
						xcss={[pressableButtonStyles, triggerWrapperStyles]}
						onClick={(event: SyntheticEvent | KeyboardEvent, analyticEvent: UIAnalyticsEvent) => {
							onTriggerAutodevClicked('AUTODEV', 'AUTODEV', event, analyticEvent);
						}}
						testId="autodev-agent-picker.ui.picker-content.run-autodev-button"
						interactionName={Interactions.AGENT_RUN_AUTODEV_BUTTON}
					>
						<Flex xcss={iconWrapperStyles} alignItems="center" justifyContent="center">
							<AngleBracketsIcon label={formatMessage(messages.runAiCodingToolButtonLabel)} />
						</Flex>
						<Stack xcss={titleSubTitleWrapperStyles}>
							<Box as="span" xcss={[subLabelStyles, truncateTextStyles]}>
								{formatMessage(messages.runAiCodingToolButtonSubheading)}
							</Box>
							<Box as="span" xcss={[truncateTextStyles]}>
								{formatMessage(messages.runAiCodingToolButtonLabel)}
							</Box>
						</Stack>
					</Pressable>
				)}
				<AgentPickerBulkOpenMenuExpSuccess />
				<AgentPickerSingleIssueOpenMenuExpSuccess />
			</Box>
			{isRestartConfirmationModalOpen && (
				<RestartConfirmationModal onRestart={restartAutodev} onCancel={cancelRestartAutodev} />
			)}
		</ShortcutScope>
	);
};

const pressableButtonStyles = xcss({
	background: 'none',
	font: 'inherit',
	cursor: 'pointer',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	display: 'flex',
	alignItems: 'center',
	gap: 'space.150',
	paddingInline: 'space.100',
	':focus': {
		// This does not work now, but it will be fixed in the future by Design system team
		// Thread: https://atlassian.slack.com/archives/CFJ9DU39U/p1744091263720689
		outlineOffset: 'space.negative.025',
	},
	':focus-visible': {
		// This does not work now, but it will be fixed in the future by Design system team
		// Thread: https://atlassian.slack.com/archives/CFJ9DU39U/p1744091263720689
		outlineOffset: 'space.negative.025',
	},
});

const triggerWrapperStyles = xcss({
	height: '48px',
	boxSizing: 'content-box',
	paddingBlock: 'space.025',
	// -(n)px should be aligned with paddingLeft+paddingRight amount ////
	width: 'calc(100% - 8px)',
	paddingLeft: 'space.100',
	paddingRight: 'space.0',
	// ////////////////////////////////////////////////////////////////
});

const searchTextWrapperStyles = xcss({
	padding: 'space.100',
});

const contentWrapper = xcss({
	maxWidth: '320px',
	width: '320px',
});

const searchIconWrapperStyles = xcss({
	display: 'flex',
	marginRight: 'space.0',
	marginLeft: 'space.075',
});

const agentLabelWrapperStyles = xcss({
	marginTop: 'space.100',
	marginBottom: 'space.100',
	paddingInline: 'space.100',
});

const agentListStyles = xcss({
	overflowY: 'auto',
	maxHeight: '180px',
	overflowX: 'hidden',
	position: 'relative',
	borderBottom: '1px solid',
	borderColor: 'color.border',
});

const spinnerWrapperStyles = xcss({
	position: 'absolute',
	top: '0',
	bottom: '0',
	left: '0',
	right: '0',
});

const spinnerWrapperHiddenStyles = xcss({
	display: 'none',
});

const truncateTextStyles = xcss({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const subLabelStyles = xcss({
	minWidth: '0',
	color: 'color.text.subtlest',
	font: token('font.body.small'),
});

const iconWrapperStyles = xcss({
	width: '34px',
});

const titleSubTitleWrapperStyles = xcss({
	minWidth: '0',
	height: '100%',
	textAlign: 'left',
	flex: 1,
	justifyContent: 'center',
});

const loadingWrapperStyles = xcss({
	width: '320px',
	height: '280px',
});
